import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useIntl } from "react-intl";
import { useMount, useUnmount } from "react-use";
import { useStores } from "../hooks";
import {
  getBookISBN,
  getLockerInfo,
  procIn,
  procOut,
  openDoor,
  closeDoor,
} from "../service";
import Header from "../component/Header";
import Footer from "../component/Footer";
import Locker from "../component/Locker";
import TakeItem from "../component/TakeItem";
import UserInfo from "../component/UserInfo";
import CountDown from "../component/CountDown";
import { SvBook, SvLockerDetail } from "../graphql";

const AdminIn = () => {
  const { formatMessage } = useIntl();
  const { sessionStore } = useStores();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [book, setBook] = useState<SvBook>();
  const [selected, setSelected] = useState<SvLockerDetail>();
  const [errorMessage, setErrorMessage] = useState("");
  const [manualIsbn, setManualIsbn] = useState("");

  useMount(() => {
    sessionStore.privateHandler = handleBookISBN;
  });

  useUnmount(() => {
    sessionStore.privateHandler = console.info;
  });

  const handleBookISBN = (barcode: string) => {
    console.info("Barcode:" + barcode);
    if (book === undefined) {
      getBookISBN(barcode).then((res) => {
        if (res === undefined) {
          setErrorMessage(
            formatMessage({
              id: "hyvendor.error.nobook",
            })
          );
        } else if (res.status === "0" || res.status === "4") {
          setBook(res);
          handleOpenDoor();
        } else {
          setErrorMessage(
            formatMessage({
              id: "hyvendor.error.bookstatus",
            })
          );
        }
      });
    }
  };

  const handleReload = () => {
    getLockerInfo().then((res) => {
      sessionStore.setLocker(
        res.locker_detail,
        res.locker.locker_tier,
        res.locker.locker_column,
        res.items
      );
    });
  };

  const handleProcIn = async () => {
    if (book !== undefined && selected !== undefined) {
      if (sessionStore.debug) {
        setIsOpen(false);
        procIn(book.id, selected.id).then((flag) => {
          if (flag) {
            setSelected({ id: selected.id, status: 1 } as SvLockerDetail);
            handleReload();
          }
        });
        return;
      }
      closeDoor(selected.id).then((flag) => {
        if (flag) {
          setIsOpen(false);
          procIn(book.id, selected.id).then((flag) => {
            if (flag) {
              setSelected({ id: selected.id, status: 1 } as SvLockerDetail);
              handleReload();
            }
          });
        } else {
          setErrorMessage(
            formatMessage({
              id: "hyvendor.error.dooropened",
            })
          );
        }
      });
    }
  };

  const handleProcOut = async () => {
    if (book !== undefined && selected !== undefined) {
      if (sessionStore.debug) {
        setIsOpen(true);
        procOut(book.id, selected.id).then((flag) => {
          if (flag) {
            handleReload();
          }
        });
        return;
      }
      openDoor(selected.id).then((flag) => {
        if (flag) {
          setIsOpen(true);
          procOut(book.id, selected.id).then((flag) => {
            if (flag) {
              handleReload();
            }
          });
        } else {
          setErrorMessage(
            formatMessage({
              id: "hyvendor.error.dooropened",
            })
          );
        }
      });
    }
  };

  const handleDoneOut = async () => {
    if (selected !== undefined) {
      if (sessionStore.debug) {
        setSelected({ id: selected.id, status: 0 } as SvLockerDetail);
        setIsOpen(false);
        setBook(undefined);
        return;
      }
      closeDoor(selected.id).then((flag) => {
        if (flag) {
          setSelected({ id: selected.id, status: 0 } as SvLockerDetail);
          setIsOpen(false);
          setBook(undefined);
        } else {
          setErrorMessage(
            formatMessage({
              id: "hyvendor.error.dooropened",
            })
          );
        }
      });
    }
  };

  const handleOpenDoor = async () => {
    if (selected?.status === 8 || selected?.status === 9){
      openDoor(selected.id);
      return;
    }
    if (sessionStore.debug) {
      setIsOpen(true);
      return;
    }
    if (selected !== undefined) {
      openDoor(selected.id).then((flag) => {
        if (flag) {
          setIsOpen(true);
        } else {
          setErrorMessage(
            formatMessage({
              id: "hyvendor.error.syserror",
            })
          );
        }
      });
    }
  };

  const handleManualIsbn = () => {
    const isbn = manualIsbn.trim();
    if (isbn !== "") {
      handleBookISBN(isbn);
      setManualIsbn("");
    }
  };

  const handleIsbnKeyPress = (key: string) => {
    setManualIsbn((prev) => prev + key);
  };

  const isbnKeys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "X"];

  const handleSelect = (book: SvBook, detail: SvLockerDetail) => {
    setBook(book);
    setSelected(detail);
  };

  return (
    <>
      <div
        className="overlay"
        style={{ display: errorMessage !== "" ? "block" : "none" }}
      >
        <div className="info">
          <section className="error">{errorMessage}</section>
          <div className="btns">
            <button
              type="button"
              className="btn confirm"
              onClick={(e) => {
                setErrorMessage("");
              }}
            >
              {formatMessage({ id: "hyvendor.button.close" })}
            </button>
          </div>
        </div>
      </div>
      <div className="page-set">
        <Header />
        <div className="Main" id="Manage">
          <UserInfo />
          <h3 className="Title">
            {formatMessage({ id: "hyvendor.title.adminin" })}
          </h3>
          <CountDown seconds={300000} />
          <section className="box-status">
            <ul>
              <li className="uping">
                {formatMessage({ id: "hyvendor.status.load" })}
              </li>
              <li className="uped">
                {formatMessage({ id: "hyvendor.status.ready" })}
              </li>
              <li className="available">
                {formatMessage({ id: "hyvendor.status.empty" })}
              </li>
              <li className="open">
                {formatMessage({ id: "hyvendor.status.opened" })}
              </li>
              <li className="abnormal">
                {formatMessage({ id: "hyvendor.status.error" })}
              </li>
              <li className="repair">
                {formatMessage({ id: "hyvendor.status.maintain" })}
              </li>
            </ul>
          </section>
          <Locker
            manage={1}
            list={sessionStore.lockerDetail}
            tier={sessionStore.lockerTier}
            column={sessionStore.lockerColumn}
            items={sessionStore.lockerItems}
            handleSelected={handleSelect}
            selectedDetail={selected}
            locked={isOpen}
          />
          <section className="book-box">
            {book !== undefined ? (
              <TakeItem item={book} />
            ) : (
              <>
                <h3 className="Title">
                  <span>請刷書籍ISBN</span>
                </h3>
                <div className="manual-isbn">
                  <input
                    type="text"
                    placeholder="請輸入ISBN"
                    value={manualIsbn}
                    onChange={(e) => setManualIsbn(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleManualIsbn();
                    }}
                  />
                </div>
                <div className="manual-isbn-pad">
                  {isbnKeys.map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleIsbnKeyPress(key)}
                    >
                      {key}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="confirm-small"
                    onClick={handleManualIsbn}
                  >
                    確認
                  </button>
                </div>
              </>
            )}
            <section className="btns">
              {!isOpen && book !== undefined && selected?.status === 1 && (
                <button
                  type="button"
                  className="btn next"
                  onClick={handleProcOut}
                >
                  {formatMessage({ id: "hyvendor.button.procout" })}
                </button>
              )}
              {!isOpen && book !== undefined && selected?.status !== 1 && (
                <button
                  type="button"
                  className="btn next"
                  onClick={handleOpenDoor}
                >
                  {formatMessage({ id: "hyvendor.button.procin" })}
                </button>
              )}
              {!isOpen && (selected?.status === 8 || selected?.status === 9) && (
                <button
                  type="button"
                  className="btn next"
                  onClick={handleOpenDoor}
                >
                  {formatMessage({ id: "hyvendor.button.open" })}
                </button>
              )}
              {isOpen && selected?.status !== 1 && (
                <button
                  type="button"
                  className="btn next"
                  onClick={handleProcIn}
                >
                  {formatMessage({ id: "hyvendor.button.donein" })}
                </button>
              )}
              {isOpen && selected?.status === 1 && (
                <button
                  type="button"
                  className="btn next"
                  onClick={handleDoneOut}
                >
                  {formatMessage({ id: "hyvendor.button.doneout" })}
                </button>
              )}
              &nbsp;
              {!isOpen && (
                <Link to="/logout">
                  <button type="button" className="btn cancel">
                    {formatMessage({ id: "hyvendor.button.cancelexit" })}
                  </button>
                </Link>
              )}
            </section>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AdminIn;
